/**
 * Public share-view bodies — the ONE live surface a public share link grants,
 * plus the MaskedPanel decoy that stands in for every other tab body.
 *
 * Design rule: nothing in here fetches. Each view is a thin `mapPublicX`
 * adapter over the already-fetched `PublicShareView` payload, feeding the SAME
 * components the authenticated app uses so the anonymous visitor sees the real
 * product, not a lookalike:
 *
 *   script   → ScriptReviewPanel        (prop-fed; portals itself)
 *   content  → ContentReviewPanel       (prop-fed; Radix Sheet portal)
 *   insights → DrawerHeader             (the CreatorInsightsDrawer's own header
 *   safety   → DrawerHeader              child — the drawer itself is welded to
 *                                        useCreatorAnalyticsMetrics / the UI
 *                                        store, so it is recomposed here from
 *                                        the same child + the same Sheet, fed
 *                                        from props)
 *   reporting→ read-only series card
 *
 * Every page-kind roster tab (creators/confirmed/scripts/content/live-content)
 * mounts its own REAL tab component instead — see CreatorsTab.tsx /
 * ConfirmedTab.tsx / ScriptsTab.tsx / ContentTab.tsx / LiveContentTab.tsx and
 * PublicCampaignShell.tsx's `PublicTabBody`. There is no roster view left in
 * this file (Task 21 removed the `PublicRosterTable` decoy that used to stand
 * in before those five migrations landed).
 *
 * BE reality this must render gracefully (see share_view_creator.go):
 *  - safety carries creator + status always, but score/summary/flags are
 *    populated ONLY once a verdict's job status is "complete" — the public
 *    path fires the SAME enqueue/fetch as an authed view when nothing has
 *    been computed yet (2026-08-19 "shortcode is the protection" policy),
 *    so a never-analyzed creator's first anonymous visit can itself start
 *    that computation — the body renders the real score/summary/flags when
 *    present and a plain "not yet available" fallback (never empty gauges)
 *    while the job is still pending.
 *  - content's `asset_url` is always null; only `url` is available.
 */
import * as React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import type {
  PublicContentHistoryItem,
  PublicContentView,
  PublicConversationView,
  PublicCreatorHeader,
  PublicGrowthPoint,
  PublicInsightsDemographics,
  PublicInsightsView,
  PublicReportingView,
  PublicSafetyFlag,
  PublicSafetyView,
  PublicScriptView,
  RosterRowRef,
} from '@discovr/features';
import { toReviewThreadEntries } from '@discovr/features';
import type { PendingPublicAction } from '@discovr/common';

import { Lock } from '../design-system/icons';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import { ChartContainer } from '../components/ui/chart';
import { ScriptReviewPanel } from '../components/common/ScriptReview';
import { ContentReviewPanel } from '../components/common/ContentReview';
import { CreatorReviewPanel } from '../components/common/CreatorReview/CreatorReviewPanel';
import { ReviewStatusPill, reviewStatusTone, type ReviewStatusTone } from '../components/common/ReviewStatusPill';
import { DrawerHeader } from './components/insights/DrawerHeader';
import type { CampaignPlatform } from '../lib/platformProfile';
import { DISPLAY_DECIMALS, formatCompact } from '../lib/format';
import { youTubeThumbnailUrl } from '../lib/youtubeThumbnail';
import { useStableCallback } from '../lib/browser/useStableCallback';
import { cn } from '../lib/utils';
import { TIME_PERIOD_OPTIONS } from '../campaign-reporting/reportingWindow';

// ─── Shared helpers ──────────────────────────────────────────────────────────

/** A genuinely inert callback for a control whose action would reach beyond
 *  what THIS link granted (e.g. switching to a sibling insights tab the
 *  grant didn't cover) — 2026-08-05 interaction-model decision: such a click
 *  does nothing, not a login prompt. Accepts (and ignores) any argument so it
 *  drops into any single-arg callback prop unchanged. */
function PUBLIC_SILENT_NOOP(..._args: unknown[]) {
  /* deliberately empty — see PublicCampaignShell.tsx's file header */
}

/** Humanize a wire status (`script_review` → `Script review`) for the review
 *  drawers, whose status pills take a label rather than a raw enum. */
export function humanizeStatus(status: string): string {
  if (!status) return '';
  const spaced = status.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Absolute date for the review drawers' "updated" slot. Public payloads carry
 *  RFC3339 timestamps; an unparseable one degrades to an em dash, never NaN. */
export function formatUpdated(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * `PublicCreatorHeader` → the minimal `RosterRowRef` shape `DrawerHeader`
 * reads. The public payload has no roster-entry id (deliberately — an
 * anonymous viewer must not learn one), so the username doubles as the key;
 * nothing downstream of the header uses it as a fetch key here.
 */
export function toRosterRowRef(creator: PublicCreatorHeader): RosterRowRef {
  return {
    id: creator.username,
    platform: {
      username: creator.username,
      name: creator.name ?? '',
      profile_picture_url: creator.avatar_url ?? '',
      is_verified: false,
    },
  };
}

// ─── Masked decoy ────────────────────────────────────────────────────────────

/**
 * Stand-in for any tab body the link did NOT grant. Deliberately a skeleton
 * (not a blank slate) so the shell keeps the real product's shape — but
 * `inert` + `aria-hidden` so a screen reader never announces a fully-working
 * decoy, and nothing inside is focusable.
 */
export function MaskedPanel({ label }: { label: string }): React.ReactElement {
  return (
    <div
      data-testid="public-masked-panel"
      inert
      aria-hidden="true"
      className="relative flex min-h-[420px] flex-col gap-4 overflow-hidden rounded-xl border border-border bg-card p-6"
    >
      <div className="flex flex-col gap-3 opacity-60 select-none">
        <Skeleton className="h-5 w-40" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <Skeleton className="h-3 w-40" />
            <Skeleton className="ml-auto h-6 w-20 rounded-full" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-card/70">
        <span className="flex size-11 items-center justify-center rounded-full bg-stone-100 text-muted-foreground">
          <Lock size={20} />
        </span>
        <p className="text-body-sm-medium text-foreground">{label} is not part of this link</p>
        <p className="text-caption-regular text-muted-foreground">Sign in to see the full campaign.</p>
      </div>
    </div>
  );
}

// ─── Creator grants ──────────────────────────────────────────────────────────

/**
 * Script grant → the real ScriptReviewPanel, open, with the SAME AI analysis
 * and GEO content the authed panel shows (2026-08-05 "share exact same
 * content on public link as we show in private link" decision — see
 * `aiAnalysis`/`geoAnalysis` below). Thread + line comments stay withheld
 * (`canAddComment`/`canAddLineComment` are independent props on
 * `ScriptReviewPanel` — `canReview` gates ONLY the header Approve button,
 * confirmed by reading the panel in full), but Approve is now a real, visible
 * action (2026-08-06 decision, mirrors CommercialPopover's Save): a public
 * visitor can click it, but it prompts sign-in instead of approving directly.
 * The click captures an `approve_script` `PendingPublicAction` so the SAME
 * approval can be resumed after sign-in — re-validated (status + version)
 * server-side via `pendingPublicActionRegistry.ts`'s `approve_script` handler.
 */
export function PublicScriptPanel({
  script,
  campCode,
  tail,
  onRequestLogin,
}: {
  script: PublicScriptView;
  /**
   * The share link's short code + path-tail (from `usePublicView()`,
   * threaded down via `PublicCampaignShell`'s `PublicGrantedOverlay`) —
   * stamped onto the `approve_script` pending action below so it can be
   * resumed against the SAME link after sign-in. Empty/undefined only
   * defensively (see `handleApprove` below); a real share link always
   * carries both.
   */
  campCode?: string;
  tail?: string;
  onRequestLogin: (action?: PendingPublicAction) => void;
}): React.ReactElement {
  const label = humanizeStatus(script.status);
  // Approve only renders once the script is in the one status that's
  // actually approvable — mirrors ScriptsTab.tsx's authed gate
  // (`canReview={!!permissions.canApproveScript && dc.status === 'script_review'}`),
  // minus the permission check: on a public link Approve is always a
  // login-prompt, never a real write, so there's no permission to gate on.
  const canApprovePublicly = script.status === 'script_review';
  const entryUsername = script.creator.username;
  const handleApprove = () => {
    if (entryUsername && campCode && tail) {
      onRequestLogin({
        type: 'approve_script',
        campCode,
        tail,
        entryUsername,
        version: script.version,
        createdAt: Date.now(),
      });
    } else {
      // Missing identifying context (shouldn't happen on a real share
      // link — defensive only, e.g. a malformed test render) — still open
      // the dialog, just without anything to resume. Same pattern as
      // CommercialPopover's handleSave.
      onRequestLogin();
    }
  };
  return (
    <ScriptReviewPanel
      open
      // Every dismissal path (Escape, scrim) resolves to the login prompt —
      // there is nothing to go back to on a public link. NOT a direct
      // `onClose={onRequestLogin}` pass-through: ScriptReviewPanel's scrim
      // wires its own onClick straight to `onClose` (a raw DOM handler, not
      // a callback ScriptReviewPanel invokes with no arguments itself), so a
      // bare pass-through would hand the click's MouseEvent through as
      // onRequestLogin's (widened) first argument — same hazard as
      // PublicCampaignShell.tsx's "Sign in" button, see that file's comment.
      onClose={() => onRequestLogin()}
      creatorName={script.creator.name || script.creator.username}
      creatorAvatarUrl={script.creator.avatar_url}
      creatorHandle={`@${script.creator.username}`}
      statusLabel={label}
      statusTone={reviewStatusTone(label)}
      versionLabel={`v${script.version}`}
      uploadedLabel={formatUpdated(script.updated_at)}
      doc={script.doc}
      // Same AI review + GEO content the authed panel shows (2026-08-05
      // parity decision) — the BE proxies the identical envelopes.
      aiAnalysis={script.ai_analysis}
      geoAnalysis={script.geo_analysis}
      showGeo={script.creator.platform.toLowerCase().includes('youtube')}
      geoReadOnly
      canReview={canApprovePublicly}
      onApprove={handleApprove}
      thread={toReviewThreadEntries(script.thread_messages)}
      hideThreadToggle={!script.thread_messages}
      canAddComment={false}
      canAddLineComment={false}
    />
  );
}

/**
 * Content grant → the real ContentReviewPanel. `asset_url` is always null
 * BE-side today, so the shared `url` is the only video source.
 *
 * Approve is now visible once the content is in the one approvable status
 * (mirrors PublicScriptPanel / ContentTab.tsx's `contentInReview` gate), and
 * — like script, since the BE fix in PR #359 added `Version` to
 * `publicContentView` (`share_view_creator.go`, `buildContentView`) — the
 * click now captures a real, resumable `approve_content` `PendingPublicAction`
 * so the same approval can be resumed after sign-in, re-validated (status +
 * version) server-side via `pendingPublicActionRegistry.ts`'s
 * `approve_content` handler. See `PublicScriptPanel`'s doc comment for the
 * identical pattern.
 */
export function PublicContentPanel({
  content,
  campCode,
  tail,
  onRequestLogin,
}: {
  content: PublicContentView;
  /**
   * The share link's short code + path-tail (from `usePublicView()`,
   * threaded down via `PublicCampaignShell`'s `PublicGrantedOverlay`) —
   * stamped onto the `approve_content` pending action below so it can be
   * resumed against the SAME link after sign-in. Empty/undefined only
   * defensively (see `handleApprove` below); a real share link always
   * carries both.
   */
  campCode?: string;
  tail?: string;
  onRequestLogin: (action?: PendingPublicAction) => void;
}): React.ReactElement {
  const canApprovePublicly = content.status === 'content_review';
  const entryUsername = content.creator.username;
  // Memoised: the panel's chat re-renders only when its thread keeps a new identity.
  const thread = React.useMemo(() => toReviewThreadEntries(content.thread_messages), [content.thread_messages]);
  const handleClose = useStableCallback(() => onRequestLogin());
  const handleApprove = useStableCallback(() => {
    if (entryUsername && campCode && tail) {
      onRequestLogin({
        type: 'approve_content',
        campCode,
        tail,
        entryUsername,
        version: content.version,
        createdAt: Date.now(),
      });
    } else {
      // Missing identifying context (shouldn't happen on a real share
      // link — defensive only, e.g. a malformed test render) — still open
      // the dialog, just without anything to resume. Same pattern as
      // PublicScriptPanel's handleApprove / CommercialPopover's handleSave.
      onRequestLogin();
    }
  });
  return (
    <ContentReviewPanel
      open
      onClose={handleClose}
      title="Content review"
      creatorName={content.creator.name || content.creator.username}
      creatorAvatarUrl={content.creator.avatar_url}
      creatorHandle={`@${content.creator.username}`}
      statusLabel={humanizeStatus(content.status)}
      updatedLabel={formatUpdated(content.updated_at)}
      videoUrl={content.url ?? content.asset_url}
      // Same AI review content the authed panel shows — see PublicScriptPanel.
      aiAnalysis={content.ai_analysis}
      // Same GEO finalize parity as PublicScriptPanel's geoAnalysis/showGeo —
      // the BE proxies the identical envelope to this public path. No
      // `entryId` and none of the three GEO action props: a public link has
      // no authed action to fire, so they stay absent/undefined and
      // ContentResultTabs hides every write affordance by construction.
      geoAnalysis={content.geo_analysis}
      showGeo={content.creator.platform.toLowerCase().includes('youtube')}
      geoReadOnly
      canReview={canApprovePublicly}
      onApprove={handleApprove}
      thread={thread}
      hideThreadToggle={!content.thread_messages}
      canAddComment={false}
      audience="brand"
    />
  );
}

/**
 * 'chat'-purpose grant → the real CreatorReviewPanel (D6). Approve/Reject are
 * real brand mutations in the authed app — in public mode they capture
 * approve_creator/reject pending actions instead (same pattern
 * PublicScriptPanel's handleApprove uses for approve_script). The thread
 * itself is read-only (D4) regardless of the D5 toggle; the toggle only
 * controls whether `messages` is present at all (see toReviewThreadEntries).
 * `canAddComment={false}` unconditionally, with no `onAddComment` — no
 * composer is ever rendered for an anonymous visitor (ChatThread's show
 * condition is `readOnly || (canAddComment && onAddComment)`; `readOnly` is
 * deliberately NOT passed here — that prop means "authed but view-only",
 * rendering a visible-but-disabled composer, which is not what an anonymous
 * public visitor gets on every other public-path panel).
 * `shareCreatorUsername` stays undefined so the authed-only re-share
 * affordance (CreatorReviewPanel's own `canShare && shareCreatorUsername`
 * gate) never renders here.
 */
export function PublicConversationPanel({
  chat,
  campCode,
  tail,
  onRequestLogin,
}: {
  chat: PublicConversationView;
  /** Same resumable-action context PublicScriptPanel/PublicContentPanel take
   *  — see those components' doc comments. */
  campCode?: string;
  tail?: string;
  onRequestLogin: (action?: PendingPublicAction) => void;
}): React.ReactElement {
  const entryUsername = chat.creator.username;
  const capture = (type: 'approve_creator' | 'reject') => () => {
    if (entryUsername && campCode && tail) {
      onRequestLogin({ type, campCode, tail, tab: 'creators', entryUsername, createdAt: Date.now() });
    } else {
      // Missing identifying context (shouldn't happen on a real share link —
      // defensive only) — still open the dialog, just without anything to
      // resume. Same pattern as PublicScriptPanel's handleApprove.
      onRequestLogin();
    }
  };
  return (
    <CreatorReviewPanel
      open
      onClose={() => onRequestLogin()}
      creatorName={chat.creator.name || chat.creator.username}
      creatorAvatarUrl={chat.creator.avatar_url}
      creatorHandle={`@${chat.creator.username}`}
      shareCreatorUsername={undefined}
      thread={toReviewThreadEntries(chat.messages)}
      threadLoading={false}
      hideThreadToggle={!chat.messages?.length}
      canAddComment={false}
      canReview
      onApprove={capture('approve_creator')}
      onReject={capture('reject')}
    />
  );
}

/** Stat strip rendered in place of DrawerHeader's Phyllo demographics row —
 *  the public payload has followers / engagement / avg views and nothing else,
 *  so four empty demographic cells would read as broken. */
function PublicStatStrip({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <div className="mt-4 grid grid-cols-3 divide-x divide-border rounded-xl border border-border px-4 py-3">
      {items.map((item) => (
        <div key={item.label} className="flex min-w-0 flex-col gap-1 px-4 first:pl-0 last:pr-0">
          <span className="truncate text-caption-regular text-muted-foreground">{item.label}</span>
          <span className="truncate text-body-md-semibold text-foreground">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Shared drawer chrome for the two creator-insight grants (insights, safety):
 *  the CreatorInsightsDrawer's own Sheet + DrawerHeader, fed from props. */
function PublicInsightsShell({
  creator,
  activeTab,
  statsSlot,
  onRequestLogin,
  children,
}: {
  creator: PublicCreatorHeader;
  activeTab: 'overview' | 'brand-safety';
  statsSlot: React.ReactNode;
  onRequestLogin: (action?: PendingPublicAction) => void;
  children: React.ReactNode;
}) {
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onRequestLogin();
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        style={{ width: 'min(1049px, calc(100vw - 13rem))' }}
        className="gap-0 p-0 sm:max-w-none max-[992px]:w-full!"
        aria-label="Creator insights"
        // Inert everywhere except the public share shell's click-capture
        // allow-list (PublicCampaignShell.tsx) — lets read-only interaction
        // inside THIS granted panel work normally on a public link.
        data-public-allowed-subtree=""
      >
        <SheetTitle className="sr-only">Creator insights</SheetTitle>
        <DrawerHeader
          creator={toRosterRowRef(creator)}
          activeTab={activeTab}
          // NOT onRequestLogin: switching to the OTHER insights tab (e.g.
          // brand-safety when only insights was granted) is reaching for
          // content this link didn't grant — inert, same as a roster row,
          // not a prompt to sign in (2026-08-05 interaction-model decision).
          onTabChange={PUBLIC_SILENT_NOOP}
          onClose={() => onRequestLogin()}
          campaignPlatform={creator.platform as CampaignPlatform}
          statsSlot={statsSlot}
        />
        <div className="flex-1 overflow-y-auto bg-muted px-6 py-5">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Creator-analysis sections (demographics / growth / content) ────────────
//
// Read-only, public-safe summaries of the SAME three things the authed
// AnalyticsTab shows — reduced to a handful of the most useful sub-widgets
// rather than replicating its dozen+ demographic cards (this is a public
// summary, not the full admin tool). Each section renders only when its data
// group is present; PublicInsightsPanel decides the overall empty-state.

/** Compact ranked row — one country/language/interest share bar. Deliberately
 *  NOT the authed RankBarRow (a chart-suite primitive with its own strong/
 *  light-color contract) — this public panel only ever needs this one plain
 *  variant. */
function PublicShareRow({ label, pct }: { label: string; pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2 text-caption-regular">
        <span className="truncate text-foreground">{label}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-[#6C09F7]"
          style={{ width: `${clamped}%`, minWidth: clamped > 0 ? 4 : 0 }}
        />
      </div>
    </div>
  );
}

/** Generic ranked-list card — feeds `PublicShareRow` from ANY row shape that
 *  carries a numeric `value`, via a caller-supplied label extractor. Lets
 *  the two real wire shapes (`PublicCodeShare`'s `code`, `PublicNamedShare`'s
 *  `name`) share one rendering path without a fake unified type standing in
 *  for both. */
function PublicNamedShareList<T extends { value: number }>({
  title,
  rows,
  getLabel,
}: {
  title: string;
  rows: T[];
  getLabel: (row: T) => string;
}) {
  const top = [...rows]
    .filter((r) => getLabel(r) && Number.isFinite(r.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);
  if (top.length === 0) return null;
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border p-5">
      <div className="text-body-sm-semibold uppercase text-foreground">{title}</div>
      <div className="flex flex-col gap-4">
        {top.map((row, i) => (
          <PublicShareRow key={`${getLabel(row)}-${i}`} label={getLabel(row)} pct={row.value} />
        ))}
      </div>
    </div>
  );
}

/** Aggregate the audience's gender mix down to a single Male/Female split —
 *  same reduction AnalyticsTab's `genderSummary` does, kept local since this
 *  panel has none of that file's tab-switching state to thread it through.
 *  Prefers `gender_distribution` (already a plain gender split) when present;
 *  falls back to collapsing `gender_age_distribution`'s age buckets when it
 *  isn't — either source, unrecognised genders fold into "female" the same
 *  way AnalyticsTab's reduction does (binary display only). */
function aggregateGenderSplit(
  demographics: PublicInsightsDemographics,
): { malePct: number; femalePct: number } | null {
  const source: Array<{ gender: string; value: number }> =
    demographics.gender_distribution && demographics.gender_distribution.length > 0
      ? demographics.gender_distribution
      : (demographics.gender_age_distribution ?? []);
  if (source.length === 0) return null;
  let male = 0;
  let female = 0;
  for (const b of source) {
    if (String(b.gender).toUpperCase() === 'MALE') male += b.value;
    else female += b.value;
  }
  const total = male + female;
  if (total <= 0) return null;
  return { malePct: Math.round((male / total) * 100), femalePct: Math.round((female / total) * 100) };
}

/** Demographics section — gender split + every named/coded breakdown the BE
 *  sends (countries, states, cities, languages, interests). Each list
 *  independently renders only when it has data, so an older/partial cached
 *  Phyllo doc still shows whatever groups it does carry. */
function PublicDemographicsSection({ demographics }: { demographics: PublicInsightsDemographics }) {
  const genderSplit = aggregateGenderSplit(demographics);
  const countries = demographics.countries ?? [];
  const states = demographics.states ?? [];
  const cities = demographics.cities ?? [];
  const languages = demographics.languages ?? [];
  const interests = demographics.interests ?? [];
  const hasAnyList =
    countries.length > 0 || states.length > 0 || cities.length > 0 || languages.length > 0 || interests.length > 0;
  if (!genderSplit && !hasAnyList) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4 text-body-md-semibold text-foreground">Audience demographics</div>
      <div className="flex flex-col gap-4">
        {genderSplit && (
          <div className="flex items-center justify-center gap-6 rounded-xl border border-border p-5 text-body-sm-medium">
            <span className="inline-flex items-center gap-2 text-foreground">
              <span className="size-[11px] shrink-0 rounded-full bg-[#9647f8]" />
              {genderSplit.malePct}% Male
            </span>
            <span className="inline-flex items-center gap-2 text-foreground">
              <span className="size-[11px] shrink-0 rounded-full bg-[#e0508a]" />
              {genderSplit.femalePct}% Female
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PublicNamedShareList title="Top countries" rows={countries} getLabel={(r) => r.code} />
          <PublicNamedShareList title="Top languages" rows={languages} getLabel={(r) => r.code} />
          <PublicNamedShareList title="Top states" rows={states} getLabel={(r) => r.name} />
          <PublicNamedShareList title="Top cities" rows={cities} getLabel={(r) => r.name} />
          <PublicNamedShareList title="Top interests" rows={interests} getLabel={(r) => r.name} />
        </div>
      </div>
    </div>
  );
}

/** Growth-history section — follower/subscriber count over time, same
 *  recharts `AreaChart` + `ChartContainer` pattern AnalyticsTab's growth
 *  chart uses (see that file's "Growth history" block), fed from the public
 *  payload's smaller point shape instead of the authed reputation series.
 *  `follower_count` (Instagram) and `subscriber_count` (YouTube) are
 *  mutually exclusive on a real point — there is no `engagement_rate` field
 *  on the wire (share_view_creator.go's `publicGrowthPoint` never had one),
 *  so this section only ever plots audience size, never an engagement line. */
function PublicGrowthSection({ growthHistory }: { growthHistory: PublicGrowthPoint[] }) {
  const points = growthHistory
    .filter((p) => p.month)
    .map((p) => ({ month: p.month, followers: p.follower_count ?? p.subscriber_count ?? 0 }));
  const hasData = points.some((p) => p.followers > 0);
  if (!hasData) return null;

  const values = points.map((p) => p.followers).filter((v) => v > 0);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const pad = Math.max((max - min) * 0.15, max * 0.02, 1);
  const domainMin = Math.max(0, Math.floor(min - pad));

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4 text-body-md-semibold text-foreground">Follower growth</div>
      <ChartContainer config={{ followers: { label: 'Followers', color: '#6C09F7' } }} className="h-40 w-full">
        <AreaChart data={points} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f2" />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#d4d4d4' }} />
          <YAxis
            domain={[domainMin, 'dataMax']}
            tick={{ fontSize: 10, fill: '#d4d4d4' }}
            tickFormatter={(v: number) => formatCompact(v)}
          />
          <Tooltip formatter={(v: unknown) => [formatCompact(Number(v)), 'Followers']} />
          <Area type="monotone" dataKey="followers" stroke="#6C09F7" fill="#EDE9FE" strokeWidth={2} dot={false} />
        </AreaChart>
      </ChartContainer>
    </div>
  );
}

function PublicContentCard({ item }: { item: PublicContentHistoryItem }) {
  const thumb = item.thumbnail_url || youTubeThumbnailUrl(item.url);
  const blurb = item.title?.trim() || item.caption?.trim() || 'Untitled';
  const publishedLabel = formatUpdated(item.published_at);
  const likes = item.engagement.likes;
  const views = item.engagement.views;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex w-[200px] shrink-0 flex-col overflow-hidden rounded-xl border border-border transition-colors"
    >
      <div className="relative aspect-video w-full bg-muted">
        {thumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            className="aspect-video w-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-2.5">
        <div className="line-clamp-2 text-caption-medium/snug text-foreground">{blurb}</div>
        <div className="mt-auto flex items-center gap-3 pt-1 text-[11px] text-muted-foreground">
          {likes != null && <span className="inline-flex items-center gap-1">♥ {formatCompact(likes)}</span>}
          {views != null && <span className="inline-flex items-center gap-1">▶ {formatCompact(views)}</span>}
          {publishedLabel !== '—' && <span className="ml-auto shrink-0">{publishedLabel}</span>}
        </div>
      </div>
    </a>
  );
}

/** The three content-history groups the BE can send on `PublicInsightsView`
 *  — mirrors `recent_content`/`top_content`/`sponsored_content`. */
type ContentGroupKey = 'recent' | 'top' | 'sponsored';

const CONTENT_GROUP_LABELS: Record<ContentGroupKey, string> = {
  recent: 'Recent',
  top: 'Top',
  sponsored: 'Sponsored',
};

/** Content-history section — a small horizontal-scroll strip of posts/
 *  videos, same card shape as the authed ContentTabsSection's cards
 *  (thumbnail, title, engagement, date). The BE now sends up to three
 *  independent lists (recent/top/sponsored); this renders whichever ones
 *  have data, with a small pill switcher when more than one group is
 *  present, and no switcher at all (just the strip) when only one is. */
function PublicContentSection({
  recentContent,
  topContent,
  sponsoredContent,
}: {
  recentContent: PublicContentHistoryItem[];
  topContent: PublicContentHistoryItem[];
  sponsoredContent: PublicContentHistoryItem[];
}) {
  const allGroups: Array<{ key: ContentGroupKey; items: PublicContentHistoryItem[] }> = [
    { key: 'recent', items: recentContent },
    { key: 'top', items: topContent },
    { key: 'sponsored', items: sponsoredContent },
  ];
  const groups = allGroups.filter((g) => g.items.length > 0);

  const [selected, setSelected] = React.useState<ContentGroupKey | null>(null);

  const [firstGroup] = groups;
  if (!firstGroup) return null;

  const activeKey = selected && groups.some((g) => g.key === selected) ? selected : firstGroup.key;
  const activeGroup = groups.find((g) => g.key === activeKey) ?? firstGroup;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="text-body-md-semibold text-foreground">Content</div>
        {groups.length > 1 && (
          <div className="flex items-center gap-1 rounded-full border border-border p-1">
            {groups.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setSelected(g.key)}
                className={cn(
                  'rounded-full px-3 py-1 text-caption-medium transition-colors',
                  g.key === activeKey
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {CONTENT_GROUP_LABELS[g.key]}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-row gap-3 overflow-x-auto pb-1">
        {activeGroup.items.slice(0, 10).map((item, i) => (
          <PublicContentCard key={`${item.url ?? 'item'}-${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}

/** Insights grant → drawer header + the three shared reach metrics, plus a
 *  read-only creator-analysis body (demographics / growth / content) when
 *  the grant's purpose covers this creator's insights — see this file's
 *  header for why that body was previously an unconditional teaser. Any
 *  combination of the three data groups may be absent (older link, or no
 *  phyllo doc yet); when ALL three are absent, a single lightweight fallback
 *  line renders instead of either crashing or an empty body. */
export function PublicInsightsPanel({
  insights,
  onRequestLogin,
}: {
  insights: PublicInsightsView;
  onRequestLogin: (action?: PendingPublicAction) => void;
}): React.ReactElement {
  const hasDemographics = !!insights.demographics;
  const hasGrowth = (insights.growth_history?.length ?? 0) > 0;
  const hasContent =
    (insights.recent_content?.length ?? 0) > 0 ||
    (insights.top_content?.length ?? 0) > 0 ||
    (insights.sponsored_content?.length ?? 0) > 0;
  const hasAnyDetail = hasDemographics || hasGrowth || hasContent;

  return (
    <PublicInsightsShell
      creator={insights.creator}
      activeTab="overview"
      onRequestLogin={onRequestLogin}
      statsSlot={
        <PublicStatStrip
          items={[
            {
              label: 'Followers',
              value: insights.followers_hidden && !insights.followers ? 'Hidden' : formatCompact(insights.followers),
            },
            { label: 'Avg. views', value: formatCompact(insights.avg_views) },
            {
              label: 'Engagement rate',
              value:
                insights.engagement_rate == null ? '—' : `${insights.engagement_rate.toFixed(DISPLAY_DECIMALS)}%`,
            },
          ]}
        />
      }
    >
      {hasAnyDetail ? (
        <div className="flex flex-col gap-4">
          {insights.demographics && <PublicDemographicsSection demographics={insights.demographics} />}
          {hasGrowth && <PublicGrowthSection growthHistory={insights.growth_history ?? []} />}
          {hasContent && (
            <PublicContentSection
              recentContent={insights.recent_content ?? []}
              topContent={insights.top_content ?? []}
              sponsoredContent={insights.sponsored_content ?? []}
            />
          )}
        </div>
      ) : (
        <PublicNotShared
          title="Detailed analysis not yet available for this creator"
          body="Audience demographics, growth and content history will appear here once available."
        />
      )}
    </PublicInsightsShell>
  );
}

// ─── Safety score / summary / flags ─────────────────────────────────────────
//
// Read-only rendering of the three optional fields on `PublicSafetyView`
// (score/summary/flags) — populated only once a brand-safety verdict's job
// reaches "complete" (the BE proxies the same AI-service endpoint the authed
// flow uses, and per the 2026-08-19 policy will itself trigger a fresh
// computation from an anonymous request when nothing exists yet — see this
// file's header). Deliberately NOT the authed
// `SafetyScoreCard`/`BrandSafetyWorkspace` (different data-fetching
// assumptions, plus write actions — Approve/Flag-for-review — that must
// never appear on a public link): this is a smaller, prop-fed, read-only-only
// summary built for this one surface.

interface SafetyBand {
  label: string;
  text: string;
  bg: string;
  border: string;
  dot: string;
}

const SAFETY_BAND_LOW: SafetyBand = {
  label: 'Low risk',
  text: 'text-emerald-700',
  bg: 'bg-emerald-50',
  border: 'border-emerald-200',
  dot: 'bg-emerald-500',
};
const SAFETY_BAND_MODERATE: SafetyBand = {
  label: 'Moderate risk',
  text: 'text-amber-700',
  bg: 'bg-amber-50',
  border: 'border-amber-200',
  dot: 'bg-amber-500',
};
const SAFETY_BAND_HIGH: SafetyBand = {
  label: 'High risk',
  text: 'text-red-700',
  bg: 'bg-red-50',
  border: 'border-red-200',
  dot: 'bg-red-500',
};
const SAFETY_BAND_BLOCKED: SafetyBand = { ...SAFETY_BAND_HIGH, label: 'Blocked' };
const SAFETY_BAND_NEEDS_REVIEW: SafetyBand = { ...SAFETY_BAND_MODERATE, label: 'Needs review' };

/** Score-band thresholds mirror BrandSafety/primitives.tsx's `ScoreRing`
 *  (>=70 good / >=45 moderate / else risky) — same bands, independently
 *  implemented with this file's Tailwind-token styling rather than that
 *  component's inline-style/hex-color approach. Used only as a fallback by
 *  `safetyTierBand` below, for a verdict predating the `tier` field. */
function safetyScoreBand(score: number): SafetyBand {
  const clamped = Math.max(0, Math.min(100, score));
  if (clamped >= 70) return SAFETY_BAND_LOW;
  if (clamped >= 45) return SAFETY_BAND_MODERATE;
  return SAFETY_BAND_HIGH;
}

/** Maps the AI service's own tier classification (PublicSafetyView.tier)
 *  directly to display-band styling, instead of re-deriving a band from
 *  `score` alone — see PublicSafetyView.tier's doc comment for why: tier
 *  and score aren't purely correlated (a hard-block hit can carry a
 *  moderate numeric score), so a re-derived band could show a reassuring
 *  "Low risk" gauge next to `summary` text describing a hard block, since
 *  `summary` is itself synthesized from this same tier on the BE. Falls
 *  back to the score-threshold bands only when `tier` is absent — an older
 *  cached verdict predating this field, or (defensively) an unrecognized
 *  tier string. */
function safetyTierBand(tier: string | undefined, score: number): SafetyBand {
  switch (tier) {
    case 'safe':
      return SAFETY_BAND_LOW;
    case 'moderate':
      return SAFETY_BAND_MODERATE;
    case 'risky':
      return SAFETY_BAND_HIGH;
    case 'blocked':
      return SAFETY_BAND_BLOCKED;
    case 'needs_review':
      return SAFETY_BAND_NEEDS_REVIEW;
    default:
      return safetyScoreBand(score);
  }
}

/** Flag severity → the SAME tinted-pill tone system `ReviewStatusPill`
 *  already renders in this panel's status strip (`reviewStatusTone` above) —
 *  reused here rather than inventing a second badge visual language.
 *  `PublicSafetyFlag.severity` is a raw BE string with no fixed enum, so an
 *  unrecognised value falls back to the neutral 'default' tone instead of
 *  guessing. */
function safetyFlagSeverityTone(severity: string): ReviewStatusTone {
  const s = severity.toLowerCase();
  if (/high|critical|severe/.test(s)) return 'rejected';
  if (/medium|moderate/.test(s)) return 'revision';
  if (/low|minor/.test(s)) return 'approved';
  return 'default';
}

function PublicSafetyScoreGauge({ score, tier }: { score: number; tier?: string }) {
  const clamped = Math.max(0, Math.min(100, score));
  const band = safetyTierBand(tier, clamped);
  return (
    <div className="flex shrink-0 flex-col items-center gap-3 rounded-xl border border-border bg-card p-6">
      <span className="text-caption-medium uppercase tracking-wide text-muted-foreground">Safety score</span>
      <div className={cn('flex size-20 items-center justify-center rounded-full border-4', band.border, band.bg)}>
        <span className={cn('text-heading-h4', band.text)}>{clamped}</span>
      </div>
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-caption-medium capitalize',
          band.border,
          band.bg,
          band.text,
        )}
      >
        <span className={cn('size-1.5 rounded-full', band.dot)} />
        {band.label}
      </span>
    </div>
  );
}

function PublicSafetyFlagsList({ flags }: { flags: PublicSafetyFlag[] }) {
  if (flags.length === 0) return null;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
      <div className="text-body-sm-semibold uppercase text-foreground">Flagged categories</div>
      <div className="flex flex-col gap-3">
        {flags.map((flag, i) => (
          <div key={`${flag.category}-${i}`} className="flex flex-col gap-1 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-body-sm-medium text-foreground">{flag.category}</span>
              <ReviewStatusPill label={flag.severity} tone={safetyFlagSeverityTone(flag.severity)} />
            </div>
            {flag.note && <p className="text-caption-regular text-muted-foreground">{flag.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The real, read-only safety breakdown — rendered only once `score` is
 *  present (see `PublicSafetyPanel` below). `summary`/`flags` may still be
 *  independently absent (an older verdict, or one with no flagged
 *  categories), so each renders only when it has something to show. */
function PublicSafetyDetail({
  score,
  tier,
  summary,
  flags,
}: {
  score: number;
  tier?: string;
  summary?: string;
  flags: PublicSafetyFlag[];
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start">
        <PublicSafetyScoreGauge score={score} tier={tier} />
        {summary && (
          <div className="flex-1 rounded-xl border border-border bg-card p-5">
            <div className="mb-2 text-body-md-semibold text-foreground">Summary</div>
            <p className="text-body-sm-regular text-muted-foreground">{summary}</p>
          </div>
        )}
      </div>
      <PublicSafetyFlagsList flags={flags} />
    </div>
  );
}

/** Safety grant → drawer header on the Brand Safety tab. Score/summary/flags
 *  render as a real, read-only breakdown once a verdict exists for this
 *  creator (`score` is not null/undefined); otherwise the body states plainly
 *  that no breakdown is available yet, same fallback tone as
 *  `PublicInsightsPanel`'s "not yet available" state — never the old
 *  unconditional "sign in to see more" teaser, since there is no login wall
 *  on this data any more. */
export function PublicSafetyPanel({
  safety,
  onRequestLogin,
}: {
  safety: PublicSafetyView;
  onRequestLogin: (action?: PendingPublicAction) => void;
}): React.ReactElement {
  const label = humanizeStatus(safety.status);
  const hasScore = safety.score != null;
  return (
    <PublicInsightsShell
      creator={safety.creator}
      activeTab="brand-safety"
      onRequestLogin={onRequestLogin}
      statsSlot={
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-border px-4 py-3">
          <span className="text-caption-regular text-muted-foreground">Review status</span>
          {label ? <ReviewStatusPill label={label} tone={reviewStatusTone(label)} /> : null}
        </div>
      }
    >
      {hasScore ? (
        <PublicSafetyDetail
          score={safety.score as number}
          tier={safety.tier}
          summary={safety.summary}
          flags={safety.flags ?? []}
        />
      ) : safety.status === 'unavailable' ? (
        // BE detected a schema-drifted verdict (job completed, but the
        // response didn't parse into anything usable) — see
        // populateSafetyVerdictFromData's doc comment on share_view_creator.go.
        // Distinct copy from the ordinary pending state below: this isn't
        // "will appear soon," it's "something went wrong reading it."
        <PublicNotShared
          title="Couldn't load this creator's safety breakdown"
          body="We ran into a problem reading the latest safety verdict for this creator. Try refreshing, or check back later."
        />
      ) : (
        <PublicNotShared
          title="Detailed safety breakdown not yet available for this creator"
          body="A safety score, summary, and any flagged categories will appear here once available for this creator."
        />
      )}
    </PublicInsightsShell>
  );
}

function PublicNotShared({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border bg-card px-6 py-16 text-center">
      <span className="flex size-11 items-center justify-center rounded-full bg-stone-100 text-muted-foreground">
        <Lock size={20} />
      </span>
      <p className="text-body-sm-semibold text-foreground">{title}</p>
      <p className="max-w-[420px] text-caption-regular text-muted-foreground">{body}</p>
    </div>
  );
}

/** One reporting series point, read defensively — the BE types `series` as an
 *  opaque array so the FE never hard-depends on a per-sub-tab shape. */
function readSeriesPoint(point: unknown, index: number): { label: string; value: string } {
  const p = (point ?? {}) as Record<string, unknown>;
  const rawLabel = p.label ?? p.name ?? p.key ?? p.date ?? `#${index + 1}`;
  const rawValue = p.value ?? p.count ?? p.total ?? p.y;
  return {
    label: String(rawLabel),
    value:
      typeof rawValue === 'number'
        ? formatCompact(rawValue)
        : rawValue == null
          ? '—'
          : String(rawValue),
  };
}

const REPORTING_SUB_LABELS: Record<string, string> = {
  overview: 'General Insights',
  sentiment: 'Sentiment Intelligence',
  audienceDynamics: 'Audience Dynamics',
  creatorContent: 'Creator Content',
  conversions: 'Conversions',
};

/** Human label for a grant's `window` value ('1w' -> '1 Week', etc.) — the
 *  SAME 4-option map the authed dropdowns render (reportingWindow.ts), so the
 *  public label can never say something the live dropdowns don't offer.
 *  `undefined`/unrecognised -> null, so callers can gate rendering on it. */
function timePeriodLabel(win: string | undefined): string | null {
  return TIME_PERIOD_OPTIONS.find((o) => o.id === win)?.label ?? null;
}

/**
 * Reporting grant → a read-only rendering of the shared per-sub series. The
 * authed reporting charts are all welded to their own fetch hooks, so this
 * renders the same numbers as a plain, self-contained list rather than forking
 * a chart it cannot feed.
 *
 * `window` (Task 12) — present only for a general-insights/sentiment grant —
 * renders as a STATIC, non-interactive label next to the title. There is no
 * `Select` here at all (not a disabled one): the BE already bounds the
 * series server-side to the granted window, so there is nothing for an
 * interactive control to do, and no client-side re-slicing happens on this
 * path.
 */
export function PublicReportingCard({
  reporting,
  window: win,
}: {
  reporting: PublicReportingView;
  window?: string;
}): React.ReactElement {
  const rows = reporting.series.map(readSeriesPoint);
  const windowLabel = timePeriodLabel(win);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
        <h2 className="text-body-md-semibold text-foreground">
          {REPORTING_SUB_LABELS[reporting.sub] ?? humanizeStatus(reporting.sub)}
        </h2>
        {windowLabel && (
          <span
            data-testid="public-reporting-window-label"
            className="inline-flex shrink-0 items-center rounded-full border border-border bg-muted px-2.5 py-1 text-caption-medium text-muted-foreground"
          >
            {windowLabel}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-body-sm-regular text-muted-foreground">
          No data was shared for this view.
        </p>
      ) : (
        <dl className="divide-y divide-border">
          {rows.map((row, i) => (
            <div key={`${row.label}-${i}`} className="flex items-center justify-between px-5 py-3.5">
              <dt className="min-w-0 truncate text-body-sm-regular text-muted-foreground">
                {row.label}
              </dt>
              <dd className="shrink-0 tabular-nums text-body-sm-medium text-foreground">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
